<template>
  <div
    ref="rowEl"
    :class="[ns.e('row'), ns.is('new', isNew), ns.is('editing', isEditing)]"
    :data-row-id="rowId"
    :style="rowStyle"
    @click.capture="onRowClickCapture"
    @dblclick.capture="onRowDblClickCapture"
    @contextmenu.capture="onRowContextMenuCapture"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div
      v-for="col in columns"
      :key="col.id"
      :class="[
        ns.e('cell'),
        ns.is('valid-error', hasCellError(col)),
        ns.is('tree-col', col.type === 'tree'),
      ]"
      :data-column-id="col.id"
      :style="bodyCellStyle(col)"
    >
      <template v-if="col.type === 'seq'">
        <span>{{ seqLabel }}</span>
      </template>
      <template v-else-if="col.type === 'tree'">
        <div
          :class="[
            ns.e('tree-cell'),
            {
              [ns.is('tree-line', showTreeLine)]: showTreeLine,
              [ns.is('show-root-line', showRootLine)]:
                showTreeLine && showRootLine,
            },
          ]"
        >
          <div v-if="showTreeLine" :class="ns.e('tree-node-wrap')">
            <span
              v-for="(continued, levelIndex) in row._treeLines"
              :key="`line-${levelIndex}`"
              :class="[ns.e('tree-indent'), ns.is('line', continued)]"
            />
            <span
              :class="[
                ns.e('tree-indent'),
                ns.is('node', true),
                ns.is('root', row._treeLevel === 0),
                ns.is('last', row._treeLast),
                ns.is('expanded', row._treeExpanded),
                ns.is('has-child', row._treeHasChild),
              ]"
            >
              <span
                :class="[
                  ns.e('tree-expand'),
                  {
                    [ns.is('expanded', row._treeExpanded)]: row._treeExpanded,
                    [ns.is('leaf', row._treeLeaf)]: row._treeLeaf,
                  },
                ]"
                @click.stop="onToggleExpand"
              />
            </span>
          </div>
          <template v-else>
            <span
              :class="[
                ns.e('tree-expand'),
                {
                  [ns.is('expanded', row._treeExpanded)]: row._treeExpanded,
                  [ns.is('leaf', row._treeLeaf)]: row._treeLeaf,
                },
              ]"
              :style="{ marginLeft: `${row._treeLevel * treeIndent}px` }"
              @click.stop="onToggleExpand"
            />
          </template>
          <grid-cell
            :row="row"
            :column="col"
            :editing="isEditing"
            :editable="isColEditable(col)"
          />
        </div>
      </template>
      <grid-cell
        v-else
        :row="row"
        :column="col"
        :editing="isEditing"
        :editable="isColEditable(col)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeUnmount, onMounted, ref } from 'vue'
import { useNamespace } from '@zoryn/hooks'
import { addUnit } from '@zoryn/utils'
import { hasPersistedRowId } from '@zoryn/components/grid/src/api/api-convention'
import {
  getColumnAlignStyle,
  isGridColumnEditable,
  resolveColumnMinWidth,
  resolveColumnWidth,
  resolveGridEditable,
} from '@zoryn/components/grid/src/column/meta'
import {
  getValidErrorKey,
  isRowEditing as checkRowEditing,
} from '@zoryn/components/grid/src/methods/edit'
import { getRowId } from '@zoryn/components/grid/src/utils/row-id'
import {
  registerRowDom,
  unregisterRowDom,
} from '@zoryn/components/grid/src/utils/row-dom-registry'
import {
  clearHoverRowDom,
  HOVER_ROW_CLASS,
  setHoverRowDom,
} from '@zoryn/components/grid/src/utils/sync-hover-row-dom'
import { CURRENT_ROW_CLASS } from '@zoryn/components/grid/src/utils/sync-current-row-dom'
import { isGridInteractiveTarget } from '@zoryn/components/grid/src/utils/is-interactive-target'
import { canOpenGridContextMenu } from '@zoryn/components/grid/src/methods/row-context-menu'
import GridCell from '@zoryn/components/grid/src/components/grid-cell.vue'
import { resolveTreeConfig } from '../tree/tree-config'
import type { TreeFlatRow } from '../tree/flatten-visible'
import type { CSSProperties } from 'vue'
import type { DataGridTree, GridColumnInfo } from '../types'

const props = defineProps<{
  row: TreeFlatRow
  index: number
  virtualStart: number
  columns: GridColumnInfo[]
  rowHeight: number
  tableWidth: number
  seqLabel: number
}>()

const emit = defineEmits<{
  click: [row: Record<string, unknown>]
  dblclick: [row: Record<string, unknown>]
}>()

const ns = useNamespace('grid')
const $grid = inject('$grid') as DataGridTree
const { reactData, internalData, props: gridProps } = $grid
const rowEl = ref<HTMLElement | null>(null)

const rowId = computed(() => getRowId(props.row, props.index))
const isNew = computed(() => !hasPersistedRowId(props.row))
const isEditing = computed(() => checkRowEditing($grid as never, props.row))
const tableEditable = computed(() =>
  resolveGridEditable(gridProps, $grid.options)
)
const treeIndent = computed(() => resolveTreeConfig($grid.options).indent)
const showTreeLine = computed(() => resolveTreeConfig($grid.options).showLine)
const showRootLine = computed(
  () => resolveTreeConfig($grid.options).showRootLine
)

const rowStyle = computed(
  () =>
    ({
      height: `${props.rowHeight}px`,
      top: `${props.virtualStart}px`,
      position: 'absolute',
      left: 0,
      minWidth: '100%',
      width: `${props.tableWidth}px`,
    }) as CSSProperties
)

function bodyCellStyle(col: GridColumnInfo): CSSProperties {
  const w = resolveColumnWidth(col)
  const style: CSSProperties = {
    width: addUnit(w),
    minWidth: addUnit(resolveColumnMinWidth(col)),
    flex: `0 0 ${addUnit(w)}`,
    ...getColumnAlignStyle(col.align),
  }
  if (col.type === 'tree') {
    style.textAlign = 'left'
    style.justifyContent = 'flex-start'
    ;(style as Record<string, string>)['--z-grid-tree-indent'] =
      `${treeIndent.value}px`
  }
  return style
}

function hasCellError(col: GridColumnInfo) {
  const key = getValidErrorKey(rowId.value, col.id)
  return !!reactData.validErrorMaps[key]
}

function isColEditable(col: GridColumnInfo) {
  return isGridColumnEditable(col, tableEditable.value)
}

function onToggleExpand() {
  $grid.toggleTreeExpand(props.row)
}

function onRowClickCapture(event: MouseEvent) {
  if (isGridInteractiveTarget(event.target)) {
    return
  }
  emit('click', props.row)
}

function onRowDblClickCapture(event: MouseEvent) {
  if (isGridInteractiveTarget(event.target)) {
    return
  }
  emit('dblclick', props.row)
}

function onRowContextMenuCapture(event: MouseEvent) {
  if (!canOpenGridContextMenu($grid as never, event)) {
    return
  }
  $grid.openContextMenu?.(event, props.row)
}

function onMouseEnter() {
  setHoverRowDom($grid as never, props.row)
}

function onMouseLeave() {
  clearHoverRowDom($grid as never)
}

onMounted(() => {
  if (rowEl.value) {
    registerRowDom(internalData as never, rowId.value, rowEl.value)
    if (reactData.currentRow === props.row) {
      rowEl.value.classList.add(CURRENT_ROW_CLASS)
    }
    if (internalData.hoverRowId === rowId.value) {
      rowEl.value.classList.add(HOVER_ROW_CLASS)
    }
  }
})

onBeforeUnmount(() => {
  if (rowEl.value) {
    unregisterRowDom(internalData as never, rowId.value, rowEl.value)
  }
})
</script>
